// Display values for referenced records, keyed by grid column and referenced record id.
//
// A foreign key cell stores the referenced record's trdm_record_id, but it *shows* that
// record's display name and business key, which the server sends alongside the row as
// `<col>_name_ref` and `<col>_<pkName>_ref`. When the user picks a new reference those
// companion fields have to change too, and the editor cannot do it by writing to its own
// `params.data`: an in-flight grid refresh (from the previous row's save) can replace the
// row's data object while the editor is open, leaving the editor holding a stale reference
// and the cell showing the old name next to the new id.
//
// So the editor records what each option looks like here, and the grid controller — which
// always has the live row node — applies the display fields at the same moment it applies
// the value.
const cache = new Map();

const cacheKey = (colId, recordId) => `${colId}::${recordId}`;

// `display` is the customProperties payload from the referenced_data endpoint:
// { display_name, key_parts }.
export function rememberFkDisplay(colId, recordId, display) {
  if (colId == null || recordId == null || recordId === '' || !display) return;
  cache.set(cacheKey(colId, recordId), {
    displayName: display.display_name ?? null,
    keyParts: Array.isArray(display.key_parts) ? display.key_parts : []
  });
}

// The `<col>_name_ref` / `<col>_<pkName>_ref` fields for a referenced record, ready to be
// merged into row data. Returns null when the record was never seen (so callers can leave
// the existing fields alone rather than blanking them on a guess).
export function fkDisplayFields(colDef, recordId) {
  const colId = colDef?.field;
  if (!colId) return null;
  const pkColumnNames = colDef.foreign_key_reference_details?.primary_key_column_names || [];

  // Clearing the reference clears everything it displayed.
  if (recordId === null || recordId === undefined || recordId === '') {
    const cleared = { [`${colId}_name_ref`]: null };
    pkColumnNames.forEach(pkName => { cleared[`${colId}_${pkName}_ref`] = null; });
    return cleared;
  }

  const entry = cache.get(cacheKey(colId, recordId));
  if (!entry) return null;

  const fields = { [`${colId}_name_ref`]: entry.displayName };
  pkColumnNames.forEach((pkName, index) => {
    fields[`${colId}_${pkName}_ref`] = entry.keyParts[index] ?? null;
  });
  return fields;
}
